import React from 'react';
import { useApolloClient } from '@apollo/client';
import { Box, Flex, IconButton, Spinner, Tooltip } from 'pounceV2';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import ConversationPanelV3 from 'Components/AI/Conversations/ConversationPanelV3';
import { useConversationMessagingV3 } from 'Components/AI/Conversations/hooks/useConversationMessagingV3';
import { useAIAttachments } from 'Components/AIExplainLogEvents/hooks';
import { KINDS_SUPPORTING_ATTACHMENTS } from 'Components/AIExplainLogEvents/utils/aiKindUtils';
import { AISummarizeKind, OutputLength, Permission } from 'Generated/schema';
import { useGetGeneralSettings } from 'Source/graphql/queries/getGeneralSettings.generated';
import { PANTHER_AI_NAME } from 'Source/constants';
import { useAIConversations } from 'Pages/AI/graphql/aiConversations.generated';
import {
  AlertDetailsDocument,
  useAlertDetails,
} from 'Pages/Alerts/AlertDetails/graphql/alertDetails.generated';
import { AlertActivityHistoryDocument } from 'Pages/Alerts/AlertDetails/common/AlertHistorySection/graphql/alertActivityHistory.generated';
import { useConversationManagementV3 } from 'Pages/AI/hooks/useConversationManagementV3';
import { ConversationalAIParams } from 'Pages/AI/types';
import useCheckPermissions from 'Hooks/useCheckPermissions';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import useUrlParams from 'Hooks/useUrlParams';
import useSidesheetV3 from 'Hooks/useSidesheetV3';
import useHover from 'Hooks/useHover';
import { printAIResponse } from 'Components/utils/printAIResponse';
import HistoryToggleButtonV3 from 'Components/buttons/HistoryToggleButton/HistoryToggleButtonV3';
import ConversationHeaderActionsV3 from 'Pages/AI/components/ConversationHeaderActionsV3';
import ConversationThreadListV3 from 'Pages/AI/components/ConversationThreadListV3';
import { conversationInput } from 'Pages/AI/graphql/aiConversations';
import {
  AlertTriageCommentRowV3,
  AlertTriageCloseActionsV3,
} from 'Components/dropdowns/AlertActions/AiSummary/AlertTriageActionsBarV3';
import AlertTriageEmptyStateV3 from './AlertTriageEmptyStateV3';
import EditableThreadNameV3 from './EditableThreadNameV3';

interface AISummaryConversationContentV3Props {
  kind: AISummarizeKind;
  alias: string;
  events?: string[];
  onClose: () => void;
  /** When true, skip existing conversation lookup and start a fresh conversation */
  forceNewConversation?: boolean;
}

/**
 * Conversational replacement for the old single-shot AISummarySidePanelContentV3.
 * Used for conversational kinds (Alert, AlertList). Looks up existing conversation
 * by alias, auto-triggers triage if none exists, and renders ConversationPanelV3.
 */
const AISummaryConversationContentV3: React.FC<AISummaryConversationContentV3Props> = ({
  kind,
  alias,
  events,
  onClose,
  forceNewConversation = false,
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const apolloClient = useApolloClient();

  const hasAlertModifyPermission = useCheckPermissions(Permission.AlertModify);

  // For Alert kind, alias IS the alert ID.
  const alertId = kind === AISummarizeKind.Alert ? alias : undefined;
  const { data: alertDetailsData } = useAlertDetails({
    variables: { id: alertId ?? '' },
    skip: !alertId,
  });
  const alertStatus = alertDetailsData?.alert?.status ?? null;

  const {
    urlParams: { conversationId: urlConversationId },
    updateUrlParams,
  } = useUrlParams<ConversationalAIParams>();
  const { state: sidesheetState } = useSidesheetV3();

  // Lookup existing conversation for this alert
  const { data, loading: lookupLoading } = useAIConversations({
    variables: conversationInput(alias, kind, 1),
  });

  const conversation = data?.aiConversations?.edges?.[0]?.node ?? null;
  const existingConversationId = conversation?.id ?? null;

  // undefined = follow URL param / server lookup, null = user wants new, string = user picked specific
  const [overrideId, setOverrideId] = React.useState<string | null | undefined>(
    forceNewConversation ? null : undefined,
  );
  const conversationId =
    overrideId === undefined ? (urlConversationId ?? existingConversationId) : overrideId;

  // Sync conversationId to URL params so refresh and direct linking work.
  // Guard with sidesheet visibility to prevent the closing animation from writing the
  // param back after handleClose has already cleared it.
  React.useEffect(() => {
    if (!sidesheetState.isVisible) {
      return;
    }
    if (conversationId && conversationId !== urlConversationId) {
      updateUrlParams({ conversationId });
    } else if (!conversationId && urlConversationId) {
      updateUrlParams({ conversationId: undefined });
    }
  }, [conversationId, urlConversationId, updateUrlParams, sidesheetState.isVisible]);

  // AlertList triage happens on the list page, not a detail page, so there are no
  // detail-specific queries to refetch. The list query auto-refreshes via its own polling.
  const handleStreamComplete = React.useCallback(() => {
    if (kind === AISummarizeKind.Alert) {
      apolloClient.refetchQueries({
        include: [AlertDetailsDocument, AlertActivityHistoryDocument],
      });
    }
  }, [apolloClient, kind]);

  const messaging = useConversationMessagingV3({
    conversationId,
    isPrivate: false,
    onConversationCreated: setOverrideId,
    onStreamComplete: handleStreamComplete,
    alias,
    kind,
    events,
    autoScrollBottom: false,
  });

  // Attachment support
  const { data: settingsData } = useGetGeneralSettings();
  const isWebAccessEnabled = settingsData?.generalSettings?.pantherAIWebTool?.enabled;
  const supportsAttachments = isWebAccessEnabled && KINDS_SUPPORTING_ATTACHMENTS.has(kind);

  const {
    attachments,
    attachmentUrls,
    addAttachment,
    removeAttachment,
    clearAttachments,
    canAttach,
    isUploading,
  } = useAIAttachments();

  // Auto-trigger triage when the sidesheet opens and no conversation exists.
  // The user already clicked the banner button to get here, so we fire immediately.
  // When forceNewConversation is true (e.g. multi-alert triage), skip the
  // existingConversationId check so a fresh triage fires even when a previous one exists.
  const hasAutoTriggered = React.useRef(false);
  const { isCreating, isStreaming, handleSendMessage, reset, cancelStream } = messaging;

  // Combined reset + auto-trigger effect.
  // Handles two concerns in a single effect to avoid a fragile declaration-order dependency:
  // 1. Reset state when the sidesheet reopens with forceNewConversation (user closed, picked
  //    different alerts, clicked "Triage with AI" again while this component instance persists).
  // 2. Auto-trigger triage when no conversation exists and the user just opened the sidesheet.
  const prevVisibleRef = React.useRef(sidesheetState.isVisible);
  React.useEffect(() => {
    // --- Reset on reopen ---
    const justOpened = sidesheetState.isVisible && !prevVisibleRef.current;
    prevVisibleRef.current = sidesheetState.isVisible;
    if (justOpened && forceNewConversation) {
      hasAutoTriggered.current = false;
      setOverrideId(null);
      reset();
      // Don't auto-trigger in the same effect run — let the next render pick up new state.
      return;
    }

    // --- Auto-trigger triage ---
    if (
      !lookupLoading &&
      (!existingConversationId || forceNewConversation) &&
      !conversationId &&
      !hasAutoTriggered.current &&
      !isCreating &&
      !isStreaming
    ) {
      hasAutoTriggered.current = true;
      handleSendMessage('', OutputLength.Large);
    }
  }, [
    sidesheetState.isVisible,
    forceNewConversation,
    reset,
    lookupLoading,
    existingConversationId,
    conversationId,
    isCreating,
    isStreaming,
    handleSendMessage,
  ]);

  // Header actions
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [isThreadListOpen, setIsThreadListOpen] = React.useState(false);
  const { handlers: hoverHandlers, isHovering } = useHover();

  // Load once when the thread list is first opened; stay loaded so title lookup
  // works after the thread list closes (prevents title reverting on conversation switch).
  const [shouldLoadConversations, setShouldLoadConversations] = React.useState(false);

  const {
    data: conversationsData,
    loading: conversationsLoading,
    fetchMore,
  } = useAIConversations({
    variables: conversationInput(alias, kind, 25),
    skip: !shouldLoadConversations,
    fetchPolicy: 'cache-and-network',
    // Flip loading=true during fetchMore so the child's useInfiniteScroll guard
    // suppresses duplicate page requests with the same cursor.
    notifyOnNetworkStatusChange: true,
  });

  const alertConversations = React.useMemo(
    () => conversationsData?.aiConversations?.edges?.map(e => e.node) ?? [],
    [conversationsData],
  );

  // Close the sidesheet when the last conversation is deleted
  const hadConversationsRef = React.useRef(false);
  React.useEffect(() => {
    if (hadConversationsRef.current || alertConversations.length === 0) {
      return;
    }
    hadConversationsRef.current = true;
  }, [alertConversations.length]);

  React.useEffect(() => {
    if (
      shouldLoadConversations &&
      !conversationsLoading &&
      hadConversationsRef.current &&
      alertConversations.length === 0 &&
      !isCreating &&
      !isStreaming
    ) {
      onClose();
    }
  }, [
    shouldLoadConversations,
    conversationsLoading,
    alertConversations.length,
    isCreating,
    isStreaming,
    onClose,
  ]);

  // Derive the active conversation from the history list (covers switching),
  // falling back to the single-lookup result, then the default title.
  const { activeConversation, isPrivate, title } = React.useMemo(() => {
    const active = conversationId
      ? (alertConversations.find(c => c.id === conversationId) ?? conversation)
      : conversation;
    return {
      activeConversation: active,
      isPrivate: active?.private ?? false,
      title: active?.title ?? `${PANTHER_AI_NAME} Triage`,
    };
  }, [alertConversations, conversationId, conversation]);

  const enablePrivacyControls = useFeatureToggle('ENABLE_AI_PRIVACY_CONTROLS');
  const canEdit = !conversationId || !enablePrivacyControls || Boolean(activeConversation?.canEdit);

  const {
    handleDelete,
    handleTogglePrivacy,
    handleCopyLink,
    isLoading: updateLoading,
  } = useConversationManagementV3({
    conversationId,
    isPrivate,
    canEdit,
    cancelStream,
    onReset: onClose,
  });

  const handlePrint = React.useCallback(() => {
    printAIResponse({ title });
  }, [title]);
  const hasNextConversationPage =
    conversationsData?.aiConversations?.pageInfo?.hasNextPage ?? false;
  const endCursor = conversationsData?.aiConversations?.pageInfo?.endCursor;

  const handleFetchMoreConversations = React.useCallback(() => {
    if (hasNextConversationPage && endCursor) {
      fetchMore({ variables: conversationInput(alias, kind, 25, endCursor) });
    }
  }, [hasNextConversationPage, endCursor, fetchMore, alias, kind]);

  const handleNewConversation = React.useCallback(() => {
    hasAutoTriggered.current = true;
    reset();
    setOverrideId(null);
    setIsThreadListOpen(false);
  }, [reset]);

  const handleSendWithAttachments = React.useCallback(
    (message: string, outputLength: OutputLength) => {
      const urls = attachmentUrls.length > 0 ? attachmentUrls : undefined;
      clearAttachments();
      return handleSendMessage(message, outputLength, urls);
    },
    [handleSendMessage, attachmentUrls, clearAttachments],
  );

  const handleStartTriage = React.useCallback(() => {
    handleSendMessage('', OutputLength.Large);
  }, [handleSendMessage]);

  const handleSelectConversation = React.useCallback((id: string) => {
    setOverrideId(id);
    setIsThreadListOpen(false);
  }, []);

  const handleConversationDeletedFromList = React.useCallback(
    (deletedId: string) => {
      if (deletedId === conversationId) {
        reset();
        setOverrideId(null);
      }
    },
    [conversationId, reset],
  );

  // Derive the last finished AI response to drive the triage comment action.
  const lastFinishedAiSummary = React.useMemo(
    () => messaging.messages.findLast(m => m.finished && m.aiResponse)?.aiResponse ?? '',
    [messaging.messages],
  );

  // Triage comment action — rendered below the last finished AI message (alert context only).
  const recommendedActionsHeader = React.useMemo(() => {
    if (!alertId || !lastFinishedAiSummary || !hasAlertModifyPermission) {
      return undefined;
    }

    return <AlertTriageCommentRowV3 alertId={alertId} aiSummary={lastFinishedAiSummary} />;
  }, [alertId, lastFinishedAiSummary, hasAlertModifyPermission]);

  // Triage close actions — rendered below the comment row (alert context only).
  const recommendedActionsRow = React.useMemo(() => {
    if (!alertId || !lastFinishedAiSummary || !alertStatus || !hasAlertModifyPermission) {
      return undefined;
    }

    return (
      <AlertTriageCloseActionsV3
        alertId={alertId}
        alertStatus={alertStatus}
        aiSummary={lastFinishedAiSummary}
        onClose={onClose}
      />
    );
  }, [alertId, alertStatus, lastFinishedAiSummary, onClose, hasAlertModifyPermission]);

  const isLookingUp = lookupLoading && !conversationId;
  // forceNewConversation permanently suppresses the empty state for this component lifecycle
  // because auto-trigger fires immediately on open — the user never needs the "start triage" UI.
  const showNewTriageEmptyState =
    overrideId === null && !isCreating && !isStreaming && !forceNewConversation;

  return (
    <Box height="100%" display="flex" flexDirection="column" position="relative">
      {/* Header */}
      <Box
        display="flex"
        component="header"
        position="sticky"
        top={0}
        zIndex={2}
        alignItems="center"
        pt="16px"
        pb="10px"
        px={6}
        borderBottom={`1px solid ${tokens.borderPrimary}`}
        sx={{
          backgroundColor: tokens.background.brand.dark.base,
        }}
        {...hoverHandlers}
      >
        {isEditingTitle ? (
          <Box>
            <EditableThreadNameV3
              conversationId={conversationId}
              conversationTitle={title}
              onCancelEdit={() => setIsEditingTitle(false)}
            />
          </Box>
        ) : (
          <Flex align="center" minHeight="38px" flex={1} minWidth={0}>
            <Flex align="center" gap={2} minWidth={0} maxWidth="70%">
              <HistoryToggleButtonV3
                isOpen={isThreadListOpen}
                onClick={() => {
                  setIsThreadListOpen(v => !v);
                  setShouldLoadConversations(true);
                }}
                disabled={isStreaming}
              />
              <Typography
                fontSize="1.25rem"
                fontWeight={400}
                lineHeight={1.5}
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
                width="100%"
              >
                {title}
              </Typography>
            </Flex>
            {!isThreadListOpen && conversationId && (
              <Box ml={2}>
                {isHovering && (
                  <Tooltip content="Rename triage" placement="top">
                    <IconButton
                      icon="pencil"
                      aria-label="Edit name"
                      variant="ghost"
                      size="medium"
                      onClick={() => setIsEditingTitle(true)}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
          </Flex>
        )}

        <Flex ml="auto" align="center" gap={2} flexShrink={0}>
          <Tooltip content="Start new triage" placement="top">
            <IconButton
              icon="plus"
              aria-label="Start new triage"
              variant="ghost"
              size="medium"
              onClick={handleNewConversation}
            />
          </Tooltip>
          {!isThreadListOpen && conversationId && activeConversation && canEdit && (
            <ConversationHeaderActionsV3
              isPrivate={isPrivate}
              isLoading={updateLoading}
              onPrint={handlePrint}
              onCopyLink={handleCopyLink}
              onTogglePrivacy={handleTogglePrivacy}
              onDelete={handleDelete}
              entityLabel="triage"
            />
          )}
          <IconButton
            icon="close"
            aria-label="Dismiss Side Panel"
            variant="outline"
            onClick={() => {
              setOverrideId(null);
              onClose();
            }}
            size="small"
          />
        </Flex>
      </Box>

      {/* Content */}
      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        {isLookingUp && (
          <Flex justify="center" align="center" flex={1} pt={10}>
            <Spinner size="medium" />
          </Flex>
        )}
        {!isLookingUp && showNewTriageEmptyState && (
          <AlertTriageEmptyStateV3
            kind={kind}
            onStartTriage={handleStartTriage}
            onSubmit={handleSendWithAttachments}
            cancelPrompt={cancelStream}
            onAttachFile={supportsAttachments ? addAttachment : undefined}
            isAttachDisabled={!canAttach}
            isUploading={isUploading}
            attachments={supportsAttachments ? attachments : undefined}
            onRemoveAttachment={supportsAttachments ? removeAttachment : undefined}
          />
        )}
        {!isLookingUp && !showNewTriageEmptyState && (
          <ConversationPanelV3
            {...messaging}
            handleSendMessage={handleSendWithAttachments}
            conversationId={conversationId}
            kind={kind}
            placeholder="Ask a follow-up question..."
            inputContainerSx={{ pl: 6, pr: 20, pb: 4 }}
            initialScrollBehavior="top"
            onAttachFile={supportsAttachments ? addAttachment : undefined}
            isAttachDisabled={!canAttach}
            isUploading={isUploading}
            attachments={supportsAttachments ? attachments : undefined}
            onRemoveAttachment={supportsAttachments ? removeAttachment : undefined}
            recommendedActionsHeader={recommendedActionsHeader}
            recommendedActionsRow={recommendedActionsRow}
          />
        )}
      </Box>

      {/* Conversation history overlay */}
      {isThreadListOpen && (
        <ConversationThreadListV3
          onClose={() => setIsThreadListOpen(false)}
          conversations={alertConversations}
          loading={conversationsLoading}
          hasNextPage={hasNextConversationPage}
          onLoadMore={handleFetchMoreConversations}
          onSelectConversation={handleSelectConversation}
          onConversationDeleted={handleConversationDeletedFromList}
          topOffset={65}
        />
      )}
    </Box>
  );
};

export default AISummaryConversationContentV3;
